import { useState, useEffect, useContext, createContext } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();

  // Fetch notifications from API
  const fetchNotifications = async (options = {}) => {
    if (!isAuthenticated || !user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: options.limit || 20,
        offset: options.offset || 0,
        unreadOnly: options.unreadOnly || false,
        ...(options.category && { category: options.category })
      });

      const response = await apiClient.get(`/api/notifications?${params}`);

      if (response.success) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
      } else {
        setError(response.message || 'Failed to fetch notifications');
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await apiClient.put(`/api/notifications/${notificationId}/read`);
      
      if (response.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
        setUnreadCount(response.data.unreadCount);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const response = await apiClient.put('/api/notifications/read-all');
      
      if (response.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => ({ 
            ...notification, 
            is_read: true, 
            read_at: new Date().toISOString() 
          }))
        );
        setUnreadCount(0);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      const response = await apiClient.delete(`/api/notifications/${notificationId}`);
      
      if (response.success) {
        // Update local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setUnreadCount(response.data.unreadCount);

        // Refresh to ensure consistency
        setTimeout(() => {
          fetchNotifications();
        }, 100);
        
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Create test notification (development only)
  const createTestNotification = async (testData = {}) => {
    try {
      const response = await apiClient.post('/api/notifications/test', {
        title: testData.title || 'Test Notification',
        message: testData.message || 'This is a test notification!',
        type: testData.type || 'info',
        category: testData.category || 'general'
      });
      
      if (response.success) {
        // Add to local state
        setNotifications(prev => [response.data.notification, ...prev]);
        setUnreadCount(response.data.unreadCount);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Get notifications by category
  const getNotificationsByCategory = (category) => {
    return notifications.filter(n => n.category === category);
  };

  // Get unread notifications
  const getUnreadNotifications = () => {
    return notifications.filter(n => !n.is_read);
  };

  // Delete all notifications
  const deleteAllNotifications = async () => {
    try {
      const response = await apiClient.delete('/api/notifications/delete-all');

      if (response.success) {
        // Clear local state
        setNotifications([]);
        setUnreadCount(0);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Auto-refresh notifications
  useEffect(() => {
    fetchNotifications();
  }, [isAuthenticated, user]);

  // Auto-refresh every 30 seconds when user is active
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const interval = setInterval(() => {
      fetchNotifications({ limit: 10 }); // Get latest 10
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    createTestNotification,
    getNotificationsByCategory,
    getUnreadNotifications,
    refresh: fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};